import { Component, Host, h, Prop, Element, Watch, State } from "@stencil/core";
import {
  HTML_TAG_FOOTER,
  HTML_TAG_SECTION,
  SECTION_SCHEME_CONFIG,
  SECTION_SCHEME_MAP,
  SECTION_VIDEO_CONTROL_COLOR_LIGHT
} from "./constants/section-constants";
import {
  HtmlTags,
  SectionLocalization,
  SectionSchemeDefinition,
  SectionSchemeName,
  SectionVideoControlColor
} from "./constants/section-types";
import pfx from "../../utils/style-class-prefix-helper";
import { SectionSchemeConfiguration } from "../../constants/colors";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { setCssVariable } from "../../utils/css-variables";
import { camelToDashCase } from "../../utils/string-format";
import { SECTION_SHOW_FOOTER_SEPARATOR } from "../../constants/global-constants";
import { SECTION_LOCALIZATION } from "./constants/section-i18n";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { sectionSvgs } from "./icon-svgs";
import { getFirstNestedChild } from "../../utils/dom-helper";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 * @slot [background-image] - holds an eon-ui-image component as background.
 */
@Component({
  tag: "eon-ui-section",
  styleUrl: "section.scss",
  shadow: true
})
export class Section
  implements
    ColorableComponent<SectionSchemeConfiguration<SectionSchemeDefinition>>,
    LocalizableComponent<SectionLocalization> {
  schemeConfig: SectionSchemeConfiguration<SectionSchemeDefinition> = SECTION_SCHEME_CONFIG;
  schemeMap = SECTION_SCHEME_MAP;

  localization: Localization<SectionLocalization> = SECTION_LOCALIZATION;
  currentLocalization: SectionLocalization;

  @Element() hostElement: HTMLEonUiSectionElement;

  @State() gridChildren;
  @State() backgroundImage;
  @State() htmlTag: HtmlTags;
  @State() sectionInSection: boolean = false; // Temporary workaround for eon.de section in section usage.
  @State() isPlaying: boolean = true;

  private ariaLabel: string;
  private gridControlChilds;
  private videoElement: HTMLEonUiVideoplayerElement;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: SectionSchemeDefinition;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ attribute: "background-color", reflect: true }) scheme: SectionSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Removes section padding at the top. Combined with remove-bottom-spacing it can be used for stages or header components.
   * @default false
   */
  @Prop() removeTopSpacing: boolean = false;

  /**
   * Removes section padding at the bottom. Combined with remove-top-spacing it can be used for stages or header components.
   * @default false
   */
  @Prop() removeBottomSpacing: boolean = false;

  /**
   * Removes outer margins from the grid for header components.
   * @default false
   */
  @Prop() fullWidth: boolean = false;

  /**
   * Removes the 1200px width restriction of the content.
   * @default false
   */
  @Prop() stretchGrid: boolean = false;

  /**
   * set this property to true if the section is used as footer.
   * @default false
   */
  @Prop() useAsFooter: boolean = false;

  /**
   * Background offset takes the color of the next section and uses it as part of this sections background. This is visible on viewport > 1920px.
   * @default false
   */
  @Prop() backgroundOffset: boolean = false;

  /**
   * Removes all horiztontal and vertical gutters from the internal grids to realize a visual appearance of components building up a wall.
   */
  @Prop() wall: boolean = false;

  /**
   * Defines color of pause/resume icon and text
   * @example <eon-ui-stage video-control-color="light"></eon-ui-stage>
   */
  @Prop({ reflect: true }) videoControlColor: SectionVideoControlColor = SECTION_VIDEO_CONTROL_COLOR_LIGHT;

  /**
   * Enables the playback option for the video. This property will only be used when video is set to the background.
   */
  @Prop() videoPlaybackOption: boolean = false;

  componentWillLoad() {
    this.applyScheme();
    this.setCurrentLocalization();
    this.gridControlChilds = this.hostElement.querySelectorAll("EON-UI-GRID-CONTROL");
    if (this.checkForParentSection(this.hostElement)) this.updateClassesForInnerSection(); // Temporary workaround for eon.de section in section usage.
    this.useTheRightHtmlTag();
    this.setImageProperties();
    if (this.wall) this.toggleWall();
    this.videoElement = getFirstNestedChild(this.hostElement, "eon-ui-videoplayer");
    this.videoElement && this.enterViewport(this.hostElement);
  }

  componentDidLoad() {
    this.setCssVariables();
    this.getBackgroundColorOfNextSection();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.scheme === "transparent" ? null : this.activeScheme.bgColor;
  }

  @Watch("useAsFooter")
  useTheRightHtmlTag() {
    if (this.useAsFooter) {
      this.ariaLabel = "footer-navigation";
      this.htmlTag = HTML_TAG_FOOTER;
    } else {
      this.htmlTag = HTML_TAG_SECTION;
    }
  }

  @Watch("wall")
  private toggleWall() {
    this.gridControlChilds.forEach((gridControl: HTMLEonUiGridControlElement) => {
      gridControl.removeBottomSpacings = this.wall;
      gridControl.removeVerticalGutter = this.wall;
      gridControl.removeHorizontalGutter = this.wall;
      gridControl.sameHeight = this.wall;
    });
  }

  private setImageProperties() {
    this.backgroundImage = Array.from(this.hostElement.children).filter.call(
      this.hostElement.children,
      function (child: HTMLElement) {
        return child.getAttribute("slot") === "background-image" || child.getAttribute("slot") === "background-video";
      }
    );
    this.backgroundImage.forEach((backgroundImage) => {
      backgroundImage.setAttribute("use-as-background", "true");
    });
  }

  @Watch("backgroundOffset")
  private getBackgroundColorOfNextSection() {
    if (this.backgroundOffset) {
      const sectionsOnPage = document.querySelectorAll("EON-UI-SECTION");
      const sectionIndex = Array.from(sectionsOnPage).indexOf(this.hostElement);
      const nextSection = sectionsOnPage[sectionIndex + 1] as HTMLEonUiSectionElement;

      if (nextSection && nextSection.scheme) {
        setCssVariable(
          this.hostElement,
          `--nextSectionBackgroundColor`,
          `var(--eon-${camelToDashCase(nextSection.scheme)})`
        );
      } else {
        setCssVariable(
          this.hostElement,
          `--nextSectionBackgroundColor`,
          `var(--${this.schemeMap.get("default").bgColor})`
        );
      }
    }
  }

  /*
   * Temporary workaround for eon.de section in section usage.
   */
  private checkForParentSection(element: HTMLElement): boolean {
    const parent = (element.parentNode as HTMLElement) || ((element as any).host as HTMLElement);

    if (!parent) return false;
    if (parent.tagName === "EON-UI-SECTION") return true;

    return this.checkForParentSection(parent);
  }

  /*
   * Temporary workaround for eon.de section in section usage.
   */
  private updateClassesForInnerSection() {
    this.sectionInSection = true;
    this.gridControlChilds.forEach((gridControl: HTMLEonUiGridControlElement) => {
      gridControl.classList.add("ignore-column-gaps");
    });
  }

  handlePlayToggle() {
    this.isPlaying ? this.videoElement.pausePlayback() : this.videoElement.startPlayback();
    this.isPlaying = !this.isPlaying;
  }

  private enterViewport(element: HTMLElement) {
    if (window && window.IntersectionObserver) {
      const videoObserver = new window.IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              this.isPlaying = true;
            } else {
              this.isPlaying = false;
            }
          });
        },
        {
          threshold: [0, 1]
        }
      );
      videoObserver.observe(element);
    }
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  render() {
    const TagForSection = `${this.htmlTag}` as keyof JSX.IntrinsicElements;
    return (
      <Host>
        <TagForSection
          class={pfx({
            "section-wrapper": true,
            "remove-top-padding": this.removeTopSpacing,
            "remove-bottom-padding": this.removeBottomSpacing || this.backgroundOffset,
            "full-width": this.fullWidth,
            "stretch-grid": this.stretchGrid,
            "background-offset": this.backgroundOffset,
            "section-in-section": this.sectionInSection // Temporary workaround for eon.de section in section usage.
          })}
          aria-label={this.ariaLabel}
        >
          <slot name="background-image"></slot>
          <div
            class={pfx({
              "section-inner": true
            })}
          >
            {SECTION_SHOW_FOOTER_SEPARATOR && this.useAsFooter && <div class={pfx("footer-separator")}></div>}
            <slot />
            {this.videoElement && this.videoPlaybackOption && (
              <button
                tabindex="0"
                class={pfx({
                  ["icon-text-wrapper"]: true,
                  [`video-control-${this.videoControlColor}`]: this.videoControlColor
                })}
                onClick={() => this.handlePlayToggle()}
              >
                <div class={pfx("icon-wrapper")}>{this.isPlaying ? sectionSvgs.pause() : sectionSvgs.play()}</div>
                <div class={pfx("text-wrapper")}>
                  <span class={pfx("text")}>
                    {this.isPlaying ? this.currentLocalization.play : this.currentLocalization.pause}
                  </span>
                </div>
              </button>
            )}
          </div>
        </TagForSection>
      </Host>
    );
  }
}
